<template>
  <div>
    <form class="form-horizontal">
      <h1 class="section-divider">
        {{ $t('question.questionInfo') }}
      </h1>
      <div class="row">
        <div class="col-sm-12 col-lg-6 col-md-12">
          <div class="form-group row">
            <label class="col-md-4 form-col-label control-label required">
              {{ $t('question.questionType') }}
              <span>
                <i
                  class="far fa-question-circle"
                  v-tooltip="
                    'Select question type. Please click “Example” to see how it looks.'
                  "
                ></i
              ></span>
            </label>
            <div class="col-md-7">
              <select
                class="form-control"
                v-model="model.question_type_id"
                :class="{ 'is-invalid': errors.has('question_type_id') }"
                :disabled="disableSelect"
              >
                <option :value="null" disabled selected style="display: none;">
                  {{ $t('select') }}
                </option>
                <option
                  v-for="(type, index) in questionTypes"
                  :key="index"
                  :value="type.question_type_id"
                >
                  {{ type.question_type_en }}
                </option>
              </select>
              <a class="pull-right" href="#" @click.prevent="example">
                <small>{{ $t('question.example') }}</small>
              </a>
              <div
                class="invalid-feedback"
                v-if="errors.has('question_type_id')"
              >
                {{ errors.first('question_type_id') }}
              </div>
            </div>
          </div>
        </div>
        <div class="col-sm-12 col-lg-6 col-md-12">
          <div class="form-group row">
            <label class="col-md-4 form-col-label control-label">
              {{ $t('question.questionCatalog') }}
              <span>
                <i
                  class="far fa-question-circle"
                  v-tooltip="
                    'Select catalog (category, subject or topic) for this question. You can also edit the catalog in future.'
                  "
                ></i></span
            ></label>
            <div class="col-md-7">
              <ts-loading-banner :loading="fetchCatalog">
                <select
                  class="form-control"
                  v-model="model.question_catalog_id"
                >
                  <option
                    :value="null"
                    disabled
                    selected
                    style="display: none;"
                  >
                    {{ $t('select') }}
                  </option>
                  <option
                    v-for="(catalog, index) in questionCatalogs"
                    :key="index"
                    :value="catalog.question_catalog_id"
                  >
                    {{ catalog.question_catalog_name_en }}
                  </option>
                </select>
              </ts-loading-banner>
              <a class="pull-right" href="#" @click.prevent="addNewCatalog">
                <small>{{ $t('question.addNewCatalog') }}</small>
              </a>
            </div>
          </div>
        </div>
      </div>
      <div class="row ts-mb-2">
        <div class="col-sm-12 col-lg-12 col-md-12">
          <div class="form-group">
            <label class="required">
              {{ $t('question.questionName') }}
              <span>
                <i
                  class="far fa-question-circle"
                  v-tooltip="
                    'Provide question here. You can add text, HTML, math and images here as question.'
                  "
                ></i></span
            ></label>
            <ckeditor
              :editor="editor"
              v-model="model.question_name"
              :config="editorConfig"
            ></ckeditor>
            <div class="validate" v-if="errors.has('question_name')">
              {{ errors.first('question_name') }}
            </div>
          </div>
        </div>
      </div>
      <!-- answer -->
      <multiple-choice
        v-model="model"
        :validate="errors"
        v-if="model.question_type_id == 1 || model.question_type_id == 2"
      />
      <multiple-correct
        v-model="model"
        :validate="errors"
        v-if="model.question_type_id == 3"
      />
      <fill-in-blank
        v-model="model"
        :validate="errors"
        v-if="model.question_type_id == 4"
      />
      <drag-and-match
        v-model="model"
        :validate="errors"
        v-if="model.question_type_id == 5"
      />
      <matching
        v-model="model"
        :validate="errors"
        v-if="model.question_type_id == 6"
      />
      <true-false
        v-model="model"
        :validate="errors"
        v-if="model.question_type_id == 8"
      />
      <yes-no
        v-model="model"
        :validate="errors"
        v-if="model.question_type_id == 9"
      />

      <explanation v-model="model" :validate="errors" />
    </form>
  </div>
</template>
<script>
import { Errors } from 'form-backend-validation'
import { mapActions, mapState } from 'vuex'
import ExampleComponent from './example'
import AddCatalogComponent from './add-catalog'
import MultipleChoice from './multiple-choice'
import MultipleCorrect from './multiple-correct'
import FillInBlank from './fill-in-blank'
import Explanation from './explanation'
import DragAndMatch from './drag-and-match'
import Matching from './matching'
import TrueFalse from './true-false'
import YesNo from './yes-no'

import Vue from 'vue'
import CKEditor from '@ckeditor/ckeditor5-vue2'
import ClassicEditor from '@ckeditor/ckeditor5-build-classic'
Vue.use(CKEditor)

export default {
  name: 'questionInfo',
  props: ['value', 'validate'],
  components: {
    MultipleChoice,
    Explanation,
    MultipleCorrect,
    FillInBlank,
    DragAndMatch,
    Matching,
    TrueFalse,
    YesNo
  },
  data() {
    return {
      fetchCatalog: false,
      editor: ClassicEditor,
      editorConfig: {
        // Editor configuration
        toolbar: [
          'heading',
          '|',
          'bold',
          'italic',
          'link',
          'bulletedList',
          'numberedList',
          'MediaEmbed',
          'blockQuote',
          'imageUpload',
          'insertTable',
          '|',
          'alignment',
          'mathType',
          'ChemType',
          'outdent',
          'indent',
          'undo',
          'redo'
        ]
      },
      errors: new Errors(),
      model: {}
    }
  },
  created() {
    this.model = this.value
    this.setEdit()
    this.fetchPreData()
  },
  computed: {
    ...mapState('eschool/eExam/question', [
      'questionTypes',
      'questionCatalogs',
      'edit_data'
    ]),
    disableSelect() {
      if (!_.isEmpty(this.edit_data)) {
        return true
      }
      return false
    }
  },
  methods: {
    ...mapActions('eschool/eExam/question', [
      'getQuestionType',
      'getQuestionCatalog'
    ]),
    async fetchPreData() {
      this.loading = true
      await this.getQuestionType()
        .then(() => {})
        .catch(error => {
          this.$toasted.error(error.message)
        })
      await this.getQuestionCatalog()
        .then(() => {})
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.loading = false
        })
    },
    fetchQuestionCatalog() {
      this.fetchCatalog = true
      this.getQuestionCatalog()
        .then(() => {})
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.fetchCatalog = false
        })
    },
    example() {
      this.$modal.show(
        ExampleComponent,
        {
          data: {}
        },
        {
          height: 'auto',
          width: '30%',
          scrollable: true,
          draggable: '.form-header',
          clickToClose: false
        }
      )
    },
    addNewCatalog() {
      this.$modal.show(
        AddCatalogComponent,
        {
          data: {}
        },
        {
          height: 'auto',
          width: '400px',
          scrollable: true,
          draggable: '.form-header',
          clickToClose: false
        },
        {
          'before-close': this.fetchQuestionCatalog
        }
      )
    },
    setEdit() {
      if (!_.isEmpty(this.edit_data) && this.edit_data !== undefined) {
        this.model.question_type_id = this.edit_data.question_type_id
        this.model.question_catalog_id = this.edit_data.question_catalog_id
        this.model.question_name = this.edit_data.question_name
      }
    }
  },
  watch: {
    validate: function(error) {
      this.errors = new Errors(error.errors)
    }
  }
}
</script>
<style scoped>
.ck.ck-editor__editable_inline {
  height: 500px;
}
.ts-h-full {
  height: auto !important;
}
</style>
